import React, { useState, useEffect, useCallback } from "react";
import Search from "../../../assets/images/comman/search-outline.svg";
import ArrowConsent from "../../../assets/images/comman/arrow-consent.svg";
import MenuHorizental from "../../../assets/images/comman/dots-horizontal-outline.svg";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";
import ConsentRequest from "../../../components/common/ConsentRequest";
import ConsentDetailsModal from "../../../components/common/ConsentDetailsModal";
import { BASE_URL } from "../../../api/apiService";
import { sanitizeSearchQuery } from "../../../utils/inputValidation";

const Consents = () => {
  const [listingData, setListingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const { toasts, removeToast, showSuccess, showError, showWarning } =
    useToast();

  // Fetch listing data on component mount
  useEffect(() => {
    fetchListingData();
  }, []);

  const formatABHANumber = (number) => {
    if (!number) return "N/A";
    const cleaned = number.replace(/\D/g, "");
    return cleaned;
  };

  // Filter data based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = listingData.filter(
        (item) =>
          item.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.abhaNumber?.includes(searchTerm) ||
          item.abhaAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.facilityName?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(listingData);
    }
  }, [searchTerm, listingData]);

  // Fetch listing data from API
  const fetchListingData = useCallback(async () => {
    try {
      setIsLoadingData(true);

      const response = await fetch(`${BASE_URL}/v3/consent-requests`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.data && result.data) {
        setListingData(result.data);
        setFilteredData(result.data);
        showSuccess(
          `Successfully loaded ${result.data.length} consent records`,
        );
      } else {
        showWarning("No consent data found");
      }
    } catch (err) {
      console.error("Error fetching listing data:", err);
      showError(`Failed to fetch consent data: ${err.message}`);
    } finally {
      setIsLoadingData(false);
    }
  }, [showSuccess, showWarning, showError]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get dynamic consent status
  const getConsentStatus = (item) => {
    const now = new Date();

    // 1. Check if dataEraseAt has passed
    if (item.dataEraseAt) {
      const dataEraseDate = new Date(item.dataEraseAt);
      if (dataEraseDate < now) {
        return "Expired";
      }
    }

    // 2. Check approved.expiryDate (preferred), then consentExpiryOn (deprecated fallback)
    const expiry = item.approved?.expiryDate || item.consentExpiryOn;
    if (expiry) {
      const expiryDate = new Date(expiry);
      if (expiryDate < now) {
        return "Expired";
      }
    }

    // 3. Check if status is REQUESTED and more than 1 hour has passed since creation
    if (item.status === "REQUESTED" && item.createdAt) {
      const createdAtDate = new Date(item.createdAt);
      const oneHourInMs = 60 * 60 * 1000;
      if (createdAtDate.getTime() + oneHourInMs < now.getTime()) {
        return "Expired";
      }
    }

    // 4. Check if consent_expiry has passed (fallback/legacy field check)
    if (item.consent_expiry) {
      const expiryDate = new Date(item.consent_expiry);
      if (expiryDate < now) {
        return "Expired";
      }
    }

    // 5. Always use the status from API response if available and not yet expired
    if (item.status) {
      return item.status;
    }

    // 6. Fallback logic if no status in API
    if (item.is_active === true) {
      return "Active";
    } else if (item.is_active === false) {
      return "Inactive";
    }

    return "Unknown";
  };

  // Get health info types from abha_details
  const getHealthInfoTypes = (item) => {
    if (!item.abha_details) return "N/A";

    const hiTypes = [];
    const abhaDetails = item.abha_details;

    // Check each possible health info type and collect them
    if (abhaDetails.OPConsultation?.hiType)
      hiTypes.push(abhaDetails.OPConsultation.hiType);
    if (abhaDetails.Prescription?.hiType)
      hiTypes.push(abhaDetails.Prescription.hiType);
    if (abhaDetails.DiagnosticReport?.hiType)
      hiTypes.push(abhaDetails.DiagnosticReport.hiType);
    if (abhaDetails.WellnessRecord?.hiType)
      hiTypes.push(abhaDetails.WellnessRecord.hiType);
    if (abhaDetails.DischargeSummary?.hiType)
      hiTypes.push(abhaDetails.DischargeSummary.hiType);
    if (abhaDetails.HealthDocumentRecord?.hiType)
      hiTypes.push(abhaDetails.HealthDocumentRecord.hiType);
    if (abhaDetails.ImmunizationRecord?.hiType)
      hiTypes.push(abhaDetails.ImmunizationRecord.hiType);
    if (abhaDetails.Invoice?.hiType) hiTypes.push(abhaDetails.Invoice.hiType);

    // Return unique hiTypes joined by comma, or 'N/A' if none found
    return hiTypes.length > 0 ? [...new Set(hiTypes)].join(", ") : "N/A";
  };

  // Get status badge class for styling
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "granted":
      case "active":
      case "approved":
        return "bg-success";
      case "requested":
        return "bg-info";
      case "initiated":
        return "bg-secondary";
      case "expired":
        return "bg-secondary text-white";
      case "revoked":
        return "bg-warning text-dark";
      case "denied":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  // Format requestPurpose for display
  const formatPurpose = (rp) => {
    if (rp === "HIMS") return "Hospital Records";
    if (rp === "PHR") return "Patient Health Records";
    return rp || "";
  };

  // Compact date format for list columns
  const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  return (
    <>
      <div className="container-fluid">
        <div className="consent-page">
          <div className="opd-filter">
            <div className="consentTitle">
              <h5>Consents</h5>
              <p className="text-muted mb-0">
                Total Records: {filteredData.length}
                {searchTerm && ` (filtered from ${listingData.length})`}
              </p>
            </div>
            <div className="opd-filter-right">
              <div className="feilds">
                <div className="searchBar">
                  <input
                    type="text"
                    placeholder="Search by Name, ABHA ID, or Facility..."
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeSearchQuery(e.target.value))
                    }
                    required
                  />
                  <span className="searchIcon">
                    <img src={Search} alt="Search" />
                  </span>
                </div>
              </div>
            </div>
            <div className="tabsOpdSectionReight">
              <button
                className="customeBtn btnLightBlueBorder me-2"
                onClick={fetchListingData}
                disabled={isLoadingData}
              >
                {isLoadingData ? "Refreshing..." : "↻ Refresh"}
              </button>
              <button
                className="customeBtn btnLightBlue"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvasRight"
                aria-controls="offcanvasRight"
              >
                + Add Consents
              </button>
            </div>
          </div>

          <div className="OpdBodySection">
            {isLoadingData ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "400px" }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading consent data...</p>
                </div>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-5">
                <div className="alert alert-info">
                  <h4>No Records Found</h4>
                  <p>
                    {searchTerm
                      ? `No records match your search criteria: "${searchTerm}"`
                      : "No consent records are available at the moment."}
                  </p>
                  {searchTerm && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setSearchTerm("")}
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <table className="table table-bordered align-middle">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Status</th>
                    <th>Purpose</th>
                    <th>HI Types</th>
                    <th>Requested Range</th>
                    <th>Expires On</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => {
                    const computedStatus = getConsentStatus(item);
                    const expiry =
                      item.approved?.expiryDate ||
                      item.consentExpiryOn ||
                      item.approved?.dataEraseAt ||
                      item.permission?.dataEraseAt;
                    return (
                      <tr key={item._id || index}>
                        <td>
                          <div className="patientDetails">
                            <h5>{item.patientName || "N/A"}</h5>

                            {(item.abhaAddress || item.patientAbhaId) && (
                              <small className="text-muted d-block">
                                {item.abhaAddress || item.patientAbhaId}
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${getStatusBadgeClass(computedStatus)}`}
                          >
                            {computedStatus}
                          </span>
                        </td>
                        <td>
                          <p className="mb-0">
                            {formatPurpose(item.requestPurpose)}
                          </p>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {item.hiTypes?.map((t, i) => (
                              <span
                                key={i}
                                className="badge bg-light text-dark border"
                                style={{ fontSize: "0.7rem" }}
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          {item.permission?.dateRange ? (
                            <small>
                              {formatDateShort(item.permission.dateRange.from)}{" "}
                              → {formatDateShort(item.permission.dateRange.to)}
                            </small>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {(() => {
                            const s = computedStatus.toUpperCase();
                            if (
                              !expiry ||
                              s === "INITIATED" ||
                              s === "REQUESTED" ||
                              s === "DENIED"
                            )
                              return "—";
                            if (s === "EXPIRED")
                              return (
                                <span className="text-muted">
                                  {formatDateShort(expiry)}
                                </span>
                              );
                            if (s === "REVOKED")
                              return (
                                <>
                                  <span>{formatDateShort(expiry)}</span>{" "}
                                  <span
                                    className="badge bg-warning text-dark"
                                    style={{ fontSize: "0.65rem" }}
                                  >
                                    Revoked
                                  </span>
                                </>
                              );
                            return formatDateShort(expiry);
                          })()}
                        </td>
                        <td>
                          <small>{formatDate(item.createdAt)}</small>
                        </td>
                        <td>
                          <div className="dropdown">
                            <div
                              className="menudropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                              style={{ cursor: "pointer" }}
                            >
                              <img src={MenuHorizental} alt="menu" />
                            </div>
                            <ul className="dropdown-menu dropdown-menu-end shadow border-0">
                              <li>
                                <button
                                  className="dropdown-item  py-1"
                                  onClick={() => {
                                    setSelectedConsent(item);
                                    setShowDetailsModal(true);
                                  }}
                                  style={{ fontSize: "16px" }}
                                >
                                  View Details
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <ConsentRequest onSuccess={fetchListingData} />

      <ConsentDetailsModal
        show={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        consent={selectedConsent}
      />

      {/* Toast Container for API and consent form notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default Consents;
